/**
 * SystemDesignPane -- Cevee's system-design whiteboard.
 *
 * Built on @xyflow/react (the lib used by Stripe / Vercel / n8n for
 * node-and-edge diagrams) instead of a generic drawing tool. Domain-fit
 * matters: candidates almost always sketch the same 7 kinds of building
 * blocks (clients, load balancers, services, caches, databases, queues,
 * external storage), so we expose them as one-click palette items.
 *
 * Wire contract (unchanged from the textarea version):
 *   onChange({ scene, summary, element_count })
 *
 *   scene  = { nodes: NodeJSON[], edges: EdgeJSON[], version: number }
 *   summary = structured-text serialization of the diagram (the AI sees
 *             this, not the visual canvas). Format:
 *               "N components: ..."
 *               "M connections: A -> B (label) ..."
 *   element_count = nodes.length + edges.length
 *
 * Keyboard:
 *   - Delete / Backspace: remove selected node(s) + edge(s)
 *
 * UX notes:
 *   - The AI sees the structured TEXT summary, not pixels. We surface a
 *     small "AI sees your design" pulse the same way as CodingPane.
 *   - We persist the whole scene JSON to the session at submit/end so
 *     the recruiter review can re-render the visual whiteboard.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Handle,
  NodeResizer,
  Position,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useReactFlow,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type EdgeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Eye,
  Send,
  Trash2,
  Smartphone,
  Globe,
  Server,
  Database,
  Layers,
  Cloud,
  Inbox,
  HardDrive,
  Square,
  StickyNote,
  Diamond,
  Boxes,
  HelpCircle,
} from 'lucide-react';

// ───────────────────────────────────────────────────────────────────
// Domain types -- a closed vocabulary of system-design components.
// Keeping the set small (and meaningful) is exactly the point: every
// node gets a domain semantic and the text summary is therefore precise.
// ───────────────────────────────────────────────────────────────────

type ComponentKind =
  | 'client'
  | 'load_balancer'
  | 'service'
  | 'cache'
  | 'database'
  | 'queue'
  | 'storage'
  | 'external'
  // Generic labeled box without a tech-specific icon. Used by non-tech
  // domain templates (consulting issue trees, marketing funnels, sales
  // account maps, finance statement skeletons) where "Database" /
  // "Service" semantics would be misleading.
  | 'box'
  // Yellow "Post-it" style note. Generic capture for ideas, hypotheses,
  // assumptions across PM / Consulting / Marketing / generic sketching.
  | 'sticky'
  // Diamond / decision shape -- "if X then Y" branches in flows + trees.
  | 'diamond'
  // OO class card -- header (class name) and body (attributes/methods).
  // Used by Object-Oriented Design + general engineering whiteboards.
  | 'class_box';

/**
 * Whiteboard "domain" -- drives the visible palette and the default
 * framework-templates ordering. Each interview family maps to one of
 * these so the surface feels purpose-built (system-design icons for
 * system design, sticky/diamond/box for product/strategy/marketing,
 * class boxes for OO engineering).
 */
export type WhiteboardDomain =
  | 'system_design'
  | 'engineering'
  | 'pm'
  | 'consulting'
  | 'finance'
  | 'marketing'
  | 'sales'
  | 'generic';

interface PaletteItem {
  kind: ComponentKind;
  labelEn: string;
  labelDe: string;
  defaultLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Tailwind classes for the icon background + ring. */
  tone: { bg: string; ring: string; icon: string };
  /**
   * Visual shape archetype on the canvas. Picked deliberately so a glance
   * tells you what kind of building block you're looking at, the way you'd
   * sketch it on a whiteboard:
   *   - rect      = generic box (services, external)
   *   - pill      = capsule (clients, load balancers)
   *   - cylinder  = stacked-oval style (databases, caches)
   *   - tape      = long horizontal with parallel hash lines (queues)
   *   - cloud     = irregular rounded blob (storage)
   *   - dashed    = rect with dashed border (external systems)
   *   - sticky    = post-it yellow note
   *   - diamond   = rotated square (decision in flows / trees)
   *   - class_box = two-compartment card (name | body) for OO classes
   */
  shape:
    | 'rect'
    | 'pill'
    | 'cylinder'
    | 'tape'
    | 'cloud'
    | 'dashed'
    | 'sticky'
    | 'diamond'
    | 'class_box';
  /**
   * Which whiteboard domain(s) this palette item appears in. We always
   * ALSO show every kind under `system_design` so the existing system-
   * design palette stays unchanged for SD interviews.
   */
  domains: WhiteboardDomain[];
}

const PALETTE: PaletteItem[] = [
  // ── System-design tech kinds (only visible on SD whiteboards) ──────
  {
    kind: 'client',
    labelEn: 'Client',
    labelDe: 'Client',
    defaultLabel: 'Client',
    icon: Smartphone,
    tone: { bg: 'bg-sky-50', ring: 'ring-sky-200', icon: 'text-sky-600' },
    shape: 'pill',
    domains: ['system_design'],
  },
  {
    kind: 'load_balancer',
    labelEn: 'Load Balancer',
    labelDe: 'Load Balancer',
    defaultLabel: 'Load Balancer',
    icon: Layers,
    tone: { bg: 'bg-indigo-50', ring: 'ring-indigo-200', icon: 'text-indigo-600' },
    shape: 'pill',
    domains: ['system_design'],
  },
  {
    kind: 'service',
    labelEn: 'Service',
    labelDe: 'Service',
    defaultLabel: 'Service',
    icon: Server,
    tone: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', icon: 'text-emerald-600' },
    shape: 'rect',
    // Engineering whiteboards (backend / fullstack / API design) also
    // benefit from a 'service' primitive, so we expose it there too.
    domains: ['system_design', 'engineering'],
  },
  {
    kind: 'cache',
    labelEn: 'Cache',
    labelDe: 'Cache',
    defaultLabel: 'Cache',
    icon: HardDrive,
    tone: { bg: 'bg-amber-50', ring: 'ring-amber-200', icon: 'text-amber-600' },
    shape: 'cylinder',
    domains: ['system_design', 'engineering'],
  },
  {
    kind: 'database',
    labelEn: 'Database',
    labelDe: 'Datenbank',
    defaultLabel: 'Database',
    icon: Database,
    tone: { bg: 'bg-rose-50', ring: 'ring-rose-200', icon: 'text-rose-600' },
    shape: 'cylinder',
    domains: ['system_design', 'engineering'],
  },
  {
    kind: 'queue',
    labelEn: 'Queue',
    labelDe: 'Queue',
    defaultLabel: 'Queue',
    icon: Inbox,
    tone: { bg: 'bg-purple-50', ring: 'ring-purple-200', icon: 'text-purple-600' },
    shape: 'tape',
    domains: ['system_design', 'engineering'],
  },
  {
    kind: 'storage',
    labelEn: 'Storage',
    labelDe: 'Storage',
    defaultLabel: 'Storage',
    icon: Cloud,
    tone: { bg: 'bg-cyan-50', ring: 'ring-cyan-200', icon: 'text-cyan-600' },
    shape: 'cloud',
    domains: ['system_design', 'engineering'],
  },
  {
    kind: 'external',
    labelEn: 'External',
    labelDe: 'Extern',
    defaultLabel: 'External',
    icon: Globe,
    tone: { bg: 'bg-slate-50', ring: 'ring-slate-200', icon: 'text-slate-600' },
    shape: 'dashed',
    domains: ['system_design', 'engineering'],
  },

  // ── Cross-domain primitives (visible on every non-SD board) ────────
  {
    kind: 'box',
    labelEn: 'Box',
    labelDe: 'Box',
    defaultLabel: 'Label',
    icon: Square,
    tone: { bg: 'bg-stone-50', ring: 'ring-stone-200', icon: 'text-stone-500' },
    shape: 'rect',
    domains: [
      'system_design',
      'engineering',
      'pm',
      'consulting',
      'finance',
      'marketing',
      'sales',
      'generic',
    ],
  },
  {
    kind: 'sticky',
    labelEn: 'Sticky',
    labelDe: 'Sticky',
    defaultLabel: 'Note',
    icon: StickyNote,
    tone: { bg: 'bg-yellow-100', ring: 'ring-yellow-300', icon: 'text-yellow-700' },
    shape: 'sticky',
    domains: ['pm', 'consulting', 'finance', 'marketing', 'sales', 'generic', 'engineering'],
  },
  {
    kind: 'diamond',
    labelEn: 'Decision',
    labelDe: 'Entscheidung',
    defaultLabel: 'Decision',
    icon: Diamond,
    tone: { bg: 'bg-violet-50', ring: 'ring-violet-200', icon: 'text-violet-600' },
    shape: 'diamond',
    domains: ['pm', 'consulting', 'finance', 'marketing', 'sales', 'generic', 'engineering'],
  },

  // ── Engineering-only: OO class diagram card (name | body) ──────────
  {
    kind: 'class_box',
    labelEn: 'Class',
    labelDe: 'Klasse',
    defaultLabel: 'ClassName | +method()',
    icon: Boxes,
    tone: { bg: 'bg-teal-50', ring: 'ring-teal-200', icon: 'text-teal-600' },
    shape: 'class_box',
    domains: ['engineering'],
  },
];

const paletteByKind = PALETTE.reduce<Record<ComponentKind, PaletteItem>>((acc, p) => {
  acc[p.kind] = p;
  return acc;
}, {} as Record<ComponentKind, PaletteItem>);

// Tailwind tone.bg class -> hex, for SVG fills + the MiniMap colour map.
// Kept in one place so both stay in lockstep when a palette tone changes.
const TONE_BG_HEX: Record<string, string> = {
  'bg-sky-50': '#f0f9ff',
  'bg-indigo-50': '#eef2ff',
  'bg-emerald-50': '#ecfdf5',
  'bg-amber-50': '#fffbeb',
  'bg-rose-50': '#fff1f2',
  'bg-purple-50': '#faf5ff',
  'bg-cyan-50': '#ecfeff',
  'bg-slate-50': '#f8fafc',
  'bg-stone-50': '#fafaf9',
  'bg-yellow-100': '#fef9c3',
  'bg-violet-50': '#f5f3ff',
  'bg-teal-50': '#f0fdfa',
};


// ───────────────────────────────────────────────────────────────────
// Custom node component -- one card with icon + editable label, plus
// the four handles so candidates can connect from any side.
// ───────────────────────────────────────────────────────────────────

interface NodeData extends Record<string, unknown> {
  kind: ComponentKind;
  label: string;
  /** Persisted width / height in pixels. Set on creation, mutated via
   * the NodeResizer drag handles on the selected node. */
  width?: number;
  height?: number;
  disabled?: boolean;
  onRename: (id: string, label: string) => void;
  onResize: (id: string, width: number, height: number) => void;
}

type ComponentNode = Node<NodeData, 'component'>;

/**
 * Per-shape styling. Each kind has a visually distinct silhouette so a
 * candidate (and a reviewer) can read the diagram at a glance the way
 * they would on a real whiteboard:
 *   - rect     : rounded-md, regular border (services)
 *   - pill     : rounded-full sides (clients, load balancers)
 *   - cylinder : top oval rendered as an absolutely-positioned ::before
 *                strip + rounded body (databases, caches)
 *   - tape     : wide pill with parallel hash lines (queues)
 *   - cloud    : extra-rounded, slightly bumpy (storage)
 *   - dashed   : dashed border (external systems)
 */
function shapeClasses(shape: PaletteItem['shape']): string {
  switch (shape) {
    case 'pill':
      return 'rounded-full';
    case 'cylinder':
      // Slight rounded-top hint, no extra padding -- the cap is a
      // ::before strip drawn on top of the shape, not extra content.
      return 'rounded-[6px] rounded-t-[10px]';
    case 'tape':
      return 'rounded';
    case 'cloud':
      return 'rounded-[14px]';
    case 'dashed':
      return 'rounded-md border-dashed';
    case 'sticky':
      // Post-it: square corners with one folded corner via a ::after
      // pseudo (we draw it as an absolutely-positioned element below).
      return 'rounded-sm';
    case 'diamond':
      // Outer wrapper stays rect; the rotated diamond is drawn as an
      // absolutely-positioned overlay so the connection handles + label
      // can stay axis-aligned (rotating the whole node breaks xyflow).
      return 'rounded-md';
    case 'class_box':
      // Two-compartment: header (class name) + body (attributes/methods).
      // Rendered with a horizontal divider via a ::before strip.
      return 'rounded-md';
    case 'rect':
    default:
      return 'rounded';
  }
}

// Per-node size presets. Smaller defaults make a 5-7 component design
// fit comfortably on one screen; larger sizes are for highlighting key
// components in a presentation context.
// Default + bounds for the freeform-resize model. Candidates drag
// the corners of a selected node to resize. Defaults are picked PER
// SHAPE so the silhouette is recognizable on first drop -- a 60x20
// diamond looks like a glitched rectangle, an 110x70 diamond looks
// like a decision symbol.
const MIN_NODE_WIDTH = 28;
const MIN_NODE_HEIGHT = 14;
const MAX_NODE_WIDTH = 480;
const MAX_NODE_HEIGHT = 240;
// Keep defaults compact so a typical 6-8 node diagram fits the side
// panel without zooming. Users can resize by dragging the corners; the
// MIN/MAX bounds above protect against accidental tiny / huge boxes.
const SHAPE_DEFAULT_SIZE: Record<PaletteItem['shape'], { w: number; h: number }> = {
  rect:      { w: 44, h: 18 },
  pill:      { w: 44, h: 18 },
  cylinder:  { w: 42, h: 22 },
  tape:      { w: 50, h: 18 },
  cloud:     { w: 46, h: 22 },
  dashed:    { w: 44, h: 18 },
  sticky:    { w: 52, h: 34 },
  diamond:   { w: 48, h: 30 },
  class_box: { w: 60, h: 34 },
};
function defaultSize(kind: ComponentKind): { w: number; h: number } {
  return SHAPE_DEFAULT_SIZE[paletteByKind[kind]?.shape || 'rect'];
}
const FALLBACK_DEFAULT = SHAPE_DEFAULT_SIZE.rect;

// Pick a text-size class based on the rendered height so the label
// scales with the box. Six buckets, micro → large. The 8px floor
// matches the new compact defaults (~h=18) — Tailwind's "text-[8px]"
// is just readable for short labels like "API" / "DB" / "Cache".
function textClassForHeight(h: number): string {
  if (h < 20) return 'text-[8px] leading-none';
  if (h < 26) return 'text-[9px] leading-none';
  if (h < 34) return 'text-[10px] leading-none';
  if (h < 48) return 'text-[11px] leading-tight';
  if (h < 80) return 'text-[12px] leading-snug';
  return 'text-[14px] leading-snug';
}
function iconClassForHeight(h: number): string {
  if (h < 20) return 'w-1.5 h-1.5';
  if (h < 26) return 'w-2 h-2';
  if (h < 34) return 'w-2.5 h-2.5';
  if (h < 48) return 'w-3 h-3';
  if (h < 80) return 'w-3.5 h-3.5';
  return 'w-[16px] h-[16px]';
}

const ComponentNodeView: React.FC<NodeProps<ComponentNode>> = ({ id, data, selected }) => {
  const palette = paletteByKind[data.kind] || paletteByKind.service;
  const Icon = palette.icon;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(data.label);

  useEffect(() => {
    setDraft(data.label);
  }, [data.label]);

  const commit = useCallback(() => {
    setEditing(false);
    const clean = draft.trim() || palette.defaultLabel;
    if (clean !== data.label) data.onRename(id, clean);
  }, [draft, data, id, palette.defaultLabel]);

  const isCylinder = palette.shape === 'cylinder';
  const isTape = palette.shape === 'tape';
  const isCloud = palette.shape === 'cloud';
  const isSticky = palette.shape === 'sticky';
  const isDiamond = palette.shape === 'diamond';
  const isClassBox = palette.shape === 'class_box';

  const fallback = SHAPE_DEFAULT_SIZE[palette.shape] || FALLBACK_DEFAULT;
  const width = data.width ?? fallback.w;
  const height = data.height ?? fallback.h;
  const textCls = textClassForHeight(height);
  const iconCls = iconClassForHeight(height);
  const fillHex = TONE_BG_HEX[palette.tone.bg] || '#f8fafc';
  const cylinderFill = fillHex;

  // Diamond/Cloud/Cylinder render their own silhouette via inline SVG;
  // the outer container must therefore be transparent so we don't see
  // a ghost rectangle wrapped around the actual shape (handles +
  // selection ring still come from the outer rect, but the visible
  // fill matches the silhouette exactly).
  const transparentContainer = isDiamond || isCloud || isCylinder;

  return (
    <div
      className={[
        'group relative transition-colors',
        'flex items-center px-1 py-0',
        isDiamond ? 'justify-center text-center' : '',
        shapeClasses(palette.shape),
        transparentContainer
          ? 'bg-transparent border-0 shadow-none'
          : [
              'border shadow-[0_1px_2px_rgba(15,23,42,0.05)]',
              palette.tone.bg,
              selected ? 'border-pink-400 ring-1 ring-pink-300/70' : 'border-white ring-1 ring-slate-200/80',
            ].join(' '),
      ].join(' ')}
      // NodeResizer drives the OUTER React Flow node width/height; we
      // mirror those into our box so the inner shape always fills it.
      style={{ width: '100%', height: '100%' }}
    >
      {/* Drag-to-resize handles around the selected node. xyflow's
          NodeResizer renders 8 invisible-ish drag points + a thin
          highlight border. We tint it pink to match the selection
          ring. The component writes width/height via the parent's
          onNodesChange callback (dimensions change), which our
          handleResize callback mirrors into data.width/data.height
          for persistence. */}
      <NodeResizer
        isVisible={!!selected && !data.disabled}
        minWidth={MIN_NODE_WIDTH}
        minHeight={MIN_NODE_HEIGHT}
        maxWidth={MAX_NODE_WIDTH}
        maxHeight={MAX_NODE_HEIGHT}
        keepAspectRatio={false}
        lineClassName="!border-pink-400/80"
        handleClassName="!bg-white !border-pink-400 !w-2.5 !h-2.5 !rounded-sm"
        onResize={(_, params) => {
          data.onResize(id, params.width, params.height);
        }}
      />

      {/* Cylinder top: thin oval strip to evoke a stacked-disk database */}
      {isCylinder && (
        <span
          aria-hidden
          className={[
            'absolute -top-1 left-2 right-2 h-2 rounded-full border pointer-events-none',
            palette.tone.bg,
            selected ? 'border-pink-300' : 'border-slate-200/80',
          ].join(' ')}
        />
      )}
      {/* Queue: subtle parallel hash lines on the left edge */}
      {isTape && (
        <span
          aria-hidden
          className="absolute left-1.5 top-1.5 bottom-1.5 w-1 flex flex-col justify-between pointer-events-none"
        >
          <span className="block h-px w-full bg-purple-300/70" />
          <span className="block h-px w-full bg-purple-300/70" />
          <span className="block h-px w-full bg-purple-300/70" />
          <span className="block h-px w-full bg-purple-300/70" />
        </span>
      )}
      {/* Cloud: SVG silhouette fills the bounding box -- no more bumps
          floating outside the click + handle area. */}
      {isCloud && (
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full pointer-events-none"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <path
            d="M 18 78 Q 4 78 4 62 Q 4 46 20 46 Q 22 28 42 28 Q 52 14 68 28 Q 86 28 86 46 Q 96 48 96 64 Q 96 78 80 78 Z"
            fill={fillHex}
            stroke={selected ? '#f472b6' : '#a5f3fc'}
            strokeWidth={2}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
      {/* Sticky: folded corner triangle (top-right) */}
      {isSticky && (
        <span
          aria-hidden
          className="absolute top-0 right-0 w-3 h-3 pointer-events-none"
          style={{
            background: 'linear-gradient(225deg, transparent 50%, rgba(0,0,0,0.06) 50%)',
          }}
        />
      )}
      {/* Diamond: SVG polygon fills the bounding box exactly so handles
          and selection ring align with the visible silhouette. We use
          stroke="non-scaling-stroke" so the border stays 1.5px regardless
          of node size. */}
      {isDiamond && (
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full pointer-events-none"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <polygon
            points="50,3 97,50 50,97 3,50"
            fill={fillHex}
            stroke={selected ? '#f472b6' : '#cbd5e1'}
            strokeWidth={2}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
      {/* Class box: thin divider strip just below the title row */}
      {isClassBox && (
        <span
          aria-hidden
          className={[
            'absolute left-1 right-1 h-px pointer-events-none',
            selected ? 'bg-pink-300' : 'bg-slate-300',
          ].join(' ')}
          style={{ top: 22 }}
        />
      )}

      {/* Connection anchors on all 4 sides */}
      <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-1.5 !h-1.5" />
      <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-1.5 !h-1.5" />
      <Handle type="target" position={Position.Left} id="l" className="!bg-slate-400 !w-1.5 !h-1.5" />
      <Handle type="source" position={Position.Right} id="r" className="!bg-slate-400 !w-1.5 !h-1.5" />

      <div className={['flex items-center gap-1 relative flex-1 min-w-0', isTape ? 'pl-2' : ''].join(' ')}>
        <Icon className={`${iconCls} flex-shrink-0 ${palette.tone.icon}`} />
        {editing ? (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setDraft(data.label);
                setEditing(false);
              }
            }}
            autoFocus
            disabled={data.disabled}
            className={`flex-1 min-w-0 bg-transparent outline-none font-medium text-slate-800 ${textCls}`}
            spellCheck={false}
          />
        ) : (
          <button
            onDoubleClick={() => !data.disabled && setEditing(true)}
            className={`flex-1 min-w-0 text-left font-medium text-slate-800 truncate cursor-text ${textCls}`}
            title="Double-click to rename - drag edges to resize"
            type="button"
          >
            {data.label}
          </button>
        )}
      </div>
    </div>
  );
};

const nodeTypes = { component: ComponentNodeView };

// ───────────────────────────────────────────────────────────────────
// Scene <-> summary helpers
// ───────────────────────────────────────────────────────────────────

function nodeLabelKind(node: Node<NodeData>): string {
  const kind = node.data?.kind || 'service';
  const human = paletteByKind[kind]?.labelEn || kind;
  return `${human}("${node.data?.label ?? ''}")`;
}

function buildSummary(nodes: Node<NodeData>[], edges: Edge[]): string {
  if (nodes.length === 0 && edges.length === 0) return '(blank canvas)';
  const idToLabel = new Map<string, string>();
  nodes.forEach((n) => idToLabel.set(n.id, nodeLabelKind(n)));
  const compLines = nodes.map((n) => `  - ${nodeLabelKind(n)}`).join('\n') || '  (none)';
  const connLines = edges
    .map((e) => {
      const s = idToLabel.get(e.source) ?? e.source;
      const t = idToLabel.get(e.target) ?? e.target;
      const raw = typeof e.label === 'string' ? e.label.trim() : '';
      const lbl = raw ? ` (${raw})` : '';
      return `  - ${s} -> ${t}${lbl}`;
    })
    .join('\n') || '  (none)';
  return (
    `Format note: each connection reads "Source -> Target (relation)".\n` +
    `Components use the form Kind("label"). Kinds are exactly: Client, Load Balancer, Service, Cache, Database, Queue, Storage, External.\n\n` +
    `${nodes.length} components:\n${compLines}\n` +
    `${edges.length} connections:\n${connLines}`
  );
}

function nodeForExport(n: Node<NodeData>) {
  // n.width / n.height come from xyflow once the user has resized;
  // before that we fall back to the data defaults.
  const fb = defaultSize(n.data.kind);
  const w = (n as Node<NodeData> & { width?: number }).width ?? n.data.width ?? fb.w;
  const h = (n as Node<NodeData> & { height?: number }).height ?? n.data.height ?? fb.h;
  return {
    id: n.id,
    kind: n.data.kind,
    label: n.data.label,
    width: w,
    height: h,
    position: n.position,
  };
}

function edgeForExport(e: Edge) {
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    label: typeof e.label === 'string' ? e.label : undefined,
  };
}

// ───────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────

interface SystemDesignPaneProps {
  lang: 'de' | 'en';
  onChange: (payload: {
    scene: { nodes: ReturnType<typeof nodeForExport>[]; edges: ReturnType<typeof edgeForExport>[]; version: number };
    summary: string;
    element_count: number;
  }) => void;
  onEvent: (action: 'submit' | 'clear' | 'mode_changed', details?: Record<string, unknown>) => void;
  disabled?: boolean;
  /** Epoch ms of last server push (drives "AI sees you" badge). */
  lastPushedAt?: number | null;
  pendingEdit?: boolean;
}

const InnerPane: React.FC<SystemDesignPaneProps> = ({
  lang,
  onChange,
  onEvent,
  disabled,
  lastPushedAt,
  pendingEdit,
}) => {
  // Tool-set the candidate has selected. Default = system_design since
  // it's the most-asked whiteboard format and exposes every primitive
  // (clients, services, DBs, queues, ...) -- candidates from other
  // domains can switch via the sidebar (and lose nothing on switch,
  // the canvas keeps everything they already drew).
  const [selectedDomain, setSelectedDomain] = useState<WhiteboardDomain | null>('system_design');
  const visiblePalette = useMemo(
    () => (selectedDomain ? PALETTE.filter((p) => p.domains.includes(selectedDomain)) : []),
    [selectedDomain],
  );
  const [nodes, setNodes] = useState<Node<NodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const idCounterRef = useRef(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { screenToFlowPosition, fitView } = useReactFlow();

  const t = useMemo(
    () => ({
      modesTitle:   lang === 'de' ? 'Tool-Set' : 'Tool set',
      modesPickHint:
        lang === 'de'
          ? 'Wähle ein Tool-Set für dieses Interview'
          : 'Pick a tool set for this interview',
      paletteTitle: lang === 'de' ? 'Komponenten' : 'Components',
      paletteHint:  lang === 'de' ? 'Klick oder drag' : 'Click or drag',
      submit:       lang === 'de' ? 'Design absenden' : 'Submit design',
      clear:        lang === 'de' ? 'Leeren' : 'Clear',
      stats: (n: number, e: number) =>
        lang === 'de'
          ? `${n} Komponente${n === 1 ? '' : 'n'} · ${e} Verbindung${e === 1 ? '' : 'en'}`
          : `${n} component${n === 1 ? '' : 's'} · ${e} connection${e === 1 ? '' : 's'}`,
      aiSeesPushed:  lang === 'de' ? 'AI sieht dein Design' : 'AI sees your design',
      aiSeesPending: lang === 'de' ? 'Wird an die AI gesendet ...' : 'Syncing to AI ...',
      aiSeesIdle:    lang === 'de' ? 'Zeichne - die AI schaut zu' : 'Draw - the AI is watching',
      hint:
        lang === 'de'
          ? 'Doppelklick auf Knoten = umbenennen · Rand → Rand = Pfeil · Pfeil anklicken = Label bearbeiten · Entf = löschen'
          : 'Double-click node = rename · Edge → edge = connect · Click an arrow = edit label · Del = remove',
      emptyNoMode:
        lang === 'de'
          ? 'Wähle links ein Tool-Set, um zu starten'
          : 'Pick a tool set on the left to start',
      emptyHasMode:
        lang === 'de'
          ? 'Klicke oder zieh eine Komponente aus der Palette'
          : 'Click or drag a component from the palette',
    }),
    [lang],
  );

  // Domain-switcher labels + descriptions. Order matches the visual
  // sidebar list -- we keep it short (2-3 words) since the sidebar is
  // narrow.
  const MODES: { id: WhiteboardDomain; label: string; sub: string }[] = useMemo(
    () => [
      { id: 'system_design', label: lang === 'de' ? 'System Design' : 'System Design', sub: lang === 'de' ? 'Service · DB · LB' : 'Service · DB · LB' },
      { id: 'engineering',   label: lang === 'de' ? 'Engineering'   : 'Engineering',   sub: lang === 'de' ? 'Klasse · API · DB' : 'Class · API · DB' },
      { id: 'pm',            label: lang === 'de' ? 'Product / PM'  : 'Product / PM',  sub: lang === 'de' ? 'Box · Sticky · Decision' : 'Box · Sticky · Decision' },
      { id: 'consulting',    label: lang === 'de' ? 'Consulting'    : 'Consulting',    sub: lang === 'de' ? 'Issue Tree · 2×2' : 'Issue tree · 2×2' },
      { id: 'finance',       label: lang === 'de' ? 'Finance'       : 'Finance',       sub: lang === 'de' ? 'Skelett · DCF'    : 'Skeleton · DCF' },
      { id: 'marketing',     label: lang === 'de' ? 'Marketing'     : 'Marketing',     sub: lang === 'de' ? 'Funnel · Channel' : 'Funnel · Channel' },
      { id: 'sales',         label: lang === 'de' ? 'Sales'         : 'Sales',         sub: lang === 'de' ? 'Pipeline · Account' : 'Pipeline · Account' },
    ],
    [lang],
  );

  // ─── Awareness badge state ────────────────────────────────────────
  const [justPushed, setJustPushed] = useState(false);
  useEffect(() => {
    if (!lastPushedAt) return;
    setJustPushed(true);
    const tt = setTimeout(() => setJustPushed(false), 1500);
    return () => clearTimeout(tt);
  }, [lastPushedAt]);

  // ─── Emit scene + summary on every change ─────────────────────────
  const sceneVersionRef = useRef(0);
  useEffect(() => {
    sceneVersionRef.current = Date.now();
    onChange({
      scene: {
        nodes: nodes.map(nodeForExport),
        edges: edges.map(edgeForExport),
        version: sceneVersionRef.current,
      },
      summary: buildSummary(nodes, edges),
      element_count: nodes.length + edges.length,
    });
    // intentionally omit onChange from deps -- parent's reference is stable
    // but if it isn't we'd push duplicates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  // ─── Mutators ─────────────────────────────────────────────────────
  const handleRename = useCallback((id: string, nextLabel: string) => {
    setNodes((curr) =>
      curr.map((n) => (n.id === id ? { ...n, data: { ...n.data, label: nextLabel } } : n)),
    );
  }, []);

  // Drag-to-resize handler. xyflow's NodeResizer fires this with the
  // new pixel width/height every frame of the drag. We mirror it into
  // data.{width,height}, the node's top-level width/height AND the
  // inline style so xyflow's renderer, the data round-trip, and the
  // inner content all agree on one source of truth.
  const handleResize = useCallback((id: string, width: number, height: number) => {
    setNodes((curr) =>
      curr.map((n) => {
        if (n.id !== id) return n;
        return {
          ...n,
          width,
          height,
          style: { ...(n.style || {}), width, height },
          data: { ...n.data, width, height },
        } as Node<NodeData>;
      }),
    );
  }, []);

  const onNodesChange = useCallback(
    (changes: NodeChange<Node<NodeData>>[]) => setNodes((curr) => applyNodeChanges(changes, curr)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((curr) => applyEdgeChanges(changes, curr)),
    [],
  );
  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((curr) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#64748b', strokeWidth: 1.8 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b', width: 18, height: 18 },
            label: '',
            labelStyle: { fill: '#334155', fontSize: 11, fontWeight: 600 },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
            labelBgPadding: [6, 3] as [number, number],
            labelBgBorderRadius: 4,
          },
          curr,
        ),
      ),
    [],
  );

  // Edge label inline editor. Selecting any single edge surfaces a
  // floating input; typing live-updates the edge label so the
  // relationship semantics ("read", "publish", "cache-miss") flow
  // straight into the AI summary the moment they're typed -- no modal
  // prompt, no Apply button.
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [edgeLabelDraft, setEdgeLabelDraft] = useState<string>('');
  const onSelectionChange = useCallback(
    ({ edges: selEdges }: { nodes: Node[]; edges: Edge[] }) => {
      if (selEdges.length === 1) {
        const e = selEdges[0];
        setSelectedEdgeId(e.id);
        setEdgeLabelDraft(typeof e.label === 'string' ? e.label : '');
      } else {
        setSelectedEdgeId(null);
      }
    },
    [],
  );
  const updateEdgeLabel = useCallback(
    (value: string) => {
      setEdgeLabelDraft(value);
      if (!selectedEdgeId) return;
      setEdges((curr) =>
        curr.map((e) =>
          e.id === selectedEdgeId ? { ...e, label: value.trim() } : e,
        ),
      );
    },
    [selectedEdgeId],
  );

  const nextId = useCallback(() => {
    idCounterRef.current += 1;
    return `n${idCounterRef.current}`;
  }, []);

  const addNode = useCallback(
    (kind: ComponentKind, pos?: { x: number; y: number }) => {
      const palette = paletteByKind[kind];
      const id = nextId();
      const center = pos ?? {
        x: 120 + Math.floor(Math.random() * 200),
        y: 80 + Math.floor(Math.random() * 200),
      };
      const { w, h } = defaultSize(kind);
      setNodes((curr) => [
        ...curr,
        {
          id,
          type: 'component',
          position: center,
          width: w,
          height: h,
          // Force xyflow to render the node at the exact pixel size
          // we ask for (otherwise it auto-fits content and our small
          // defaults get blown up by the inner flex children).
          style: { width: w, height: h },
          data: {
            kind,
            label: palette.defaultLabel,
            width: w,
            height: h,
            disabled,
            onRename: handleRename,
            onResize: handleResize,
          },
        } as Node<NodeData>,
      ]);
    },
    [nextId, handleRename, handleResize, disabled],
  );

  const handleClear = useCallback(() => {
    setNodes([]);
    setEdges([]);
    idCounterRef.current = 0;
    onEvent('clear');
  }, [onEvent]);


  const handleSubmit = useCallback(() => {
    onEvent('submit', {
      node_count: nodes.length,
      edge_count: edges.length,
    });
  }, [nodes.length, edges.length, onEvent]);

  // ─── Drag & drop from palette ────────────────────────────────────
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLButtonElement>, kind: ComponentKind) => {
      e.dataTransfer.setData('application/cevee-node', kind);
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData('application/cevee-node') as ComponentKind;
      if (!kind || !paletteByKind[kind]) return;
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      addNode(kind, pos);
    },
    [addNode, screenToFlowPosition],
  );

  const awarenessLabel = pendingEdit
    ? t.aiSeesPending
    : lastPushedAt
      ? t.aiSeesPushed
      : t.aiSeesIdle;

  return (
    <div className="relative z-0 flex flex-col h-full bg-white rounded-2xl border border-white/10 overflow-hidden isolate">
      {/* Header / toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50/60 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className={[
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border whitespace-nowrap transition-colors flex-shrink-0',
              justPushed
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : pendingEdit
                  ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-white border-slate-200 text-slate-500',
            ].join(' ')}
            title={awarenessLabel}
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className={[
                  'absolute inline-flex h-full w-full rounded-full opacity-75',
                  justPushed
                    ? 'bg-emerald-500 animate-ping'
                    : pendingEdit
                      ? 'bg-amber-500 animate-pulse'
                      : 'bg-slate-300',
                ].join(' ')}
              />
              <span
                className={[
                  'relative inline-flex h-1.5 w-1.5 rounded-full',
                  justPushed ? 'bg-emerald-500' : pendingEdit ? 'bg-amber-500' : 'bg-slate-400',
                ].join(' ')}
              />
            </span>
            <Eye className="w-3 h-3" />
            {awarenessLabel}
          </span>
          <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5 min-w-0">
            <span className="relative inline-flex group/hint">
              <button
                type="button"
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label={lang === 'de' ? 'Tipps' : 'Tips'}
                title={t.hint}
              >
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <span className="absolute left-0 top-full mt-1 z-30 hidden group-hover/hint:block pointer-events-none">
                <span className="block whitespace-nowrap rounded-md bg-slate-900 text-white text-[10px] font-medium px-2 py-1 shadow-lg">
                  {t.hint}
                </span>
              </span>
            </span>
            <span className="text-slate-400 truncate">{t.stats(nodes.length, edges.length)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleClear}
            disabled={disabled || (nodes.length === 0 && edges.length === 0)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
          >
            <Trash2 className="w-3 h-3" />
            {t.clear}
          </button>
          <button
            onClick={handleSubmit}
            disabled={disabled || nodes.length === 0}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 disabled:opacity-40"
          >
            <Send className="w-3 h-3" />
            {t.submit}
          </button>
        </div>
      </div>

      {/* Body: sidebar (modes + palette) + canvas */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar: tool-set picker on top, palette below */}
        <div className="w-48 flex-shrink-0 border-r border-slate-200 bg-slate-50/40 overflow-y-auto">
          {/* Tool-set picker */}
          <div className="p-2 border-b border-slate-200/70">
            <p className="px-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">
              {t.modesTitle}
            </p>
            <p className="px-1 text-[10px] text-slate-400 mt-0.5 mb-1.5">
              {t.modesPickHint}
            </p>
            <div className="space-y-0.5">
              {MODES.map((m) => {
                const active = selectedDomain === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      if (disabled) return;
                      const next = active ? null : m.id;
                      setSelectedDomain(next);
                      onEvent('mode_changed', { domain: next });
                    }}
                    disabled={disabled}
                    className={[
                      'w-full text-left px-2 py-1 rounded-md transition-colors disabled:opacity-40',
                      active
                        ? 'bg-pink-50 ring-1 ring-pink-300 text-pink-700'
                        : 'text-slate-600 hover:bg-white hover:text-slate-800',
                    ].join(' ')}
                  >
                    <div className="text-[12px] font-semibold leading-tight">{m.label}</div>
                    <div className={['text-[9px] leading-tight mt-0.5', active ? 'text-pink-500/80' : 'text-slate-400'].join(' ')}>
                      {m.sub}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Palette (only renders once a tool-set is selected) */}
          {selectedDomain ? (
            <div className="p-2 space-y-1">
              <div className="px-1 pb-1">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                  {t.paletteTitle}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">{t.paletteHint}</p>
              </div>
              {visiblePalette.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.kind}
                    type="button"
                    draggable={!disabled}
                    onDragStart={(e) => handleDragStart(e, p.kind)}
                    onClick={() => !disabled && addNode(p.kind)}
                    disabled={disabled}
                    className={[
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs font-medium text-slate-700 border bg-white hover:bg-slate-50 transition-colors disabled:opacity-40',
                      p.tone.ring,
                    ].join(' ')}
                  >
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${p.tone.icon}`} />
                    <span className="truncate">{lang === 'de' ? p.labelDe : p.labelEn}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-3">
              <p className="text-[10px] text-slate-400 leading-relaxed">
                {t.modesPickHint}
              </p>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div
          ref={wrapperRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="flex-1 min-w-0 relative"
        >
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <p className="text-xs text-slate-400 italic px-4 text-center">
                {selectedDomain ? t.emptyHasMode : t.emptyNoMode}
              </p>
            </div>
          )}
          {selectedEdgeId && (
            <div className="absolute top-3 right-3 z-20 flex items-center gap-2 bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md px-2.5 py-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {lang === 'de' ? 'Label' : 'Label'}
              </span>
              <input
                value={edgeLabelDraft}
                onChange={(e) => updateEdgeLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    (e.target as HTMLInputElement).blur();
                    setSelectedEdgeId(null);
                  }
                }}
                placeholder={
                  lang === 'de'
                    ? 'z.B. read, write, publish'
                    : 'e.g. read, write, publish'
                }
                disabled={disabled}
                autoFocus
                className="px-2 py-0.5 text-xs text-slate-700 border border-slate-200 rounded-md outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-200 w-52"
              />
              <button
                type="button"
                onClick={() => setSelectedEdgeId(null)}
                className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 px-1"
                title={lang === 'de' ? 'Schließen' : 'Close'}
              >
                ×
              </button>
            </div>
          )}
          <ReactFlow<Node<NodeData>>
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onSelectionChange={onSelectionChange}
            defaultEdgeOptions={{
              type: 'smoothstep',
              markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b', width: 18, height: 18 },
              style: { stroke: '#64748b', strokeWidth: 1.8 },
            }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            deleteKeyCode={['Delete', 'Backspace']}
            nodesDraggable={!disabled}
            nodesConnectable={!disabled}
            elementsSelectable={!disabled}
          >
            <Background gap={16} size={1} color="#e2e8f0" />
            <Controls
              showInteractive={false}
              className="!bg-white !border !border-slate-200 !rounded-lg !shadow-sm"
            />
            <MiniMap
              zoomable
              pannable
              nodeStrokeWidth={2}
              nodeColor={(node) => {
                const kind = (node.data as NodeData | undefined)?.kind || 'service';
                const tone = paletteByKind[kind]?.tone.bg || 'bg-slate-50';
                return TONE_BG_HEX[tone] || '#f1f5f9';
              }}
              className="!bg-white/70 !border !border-slate-200 !rounded-lg"
            />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

const SystemDesignPane: React.FC<SystemDesignPaneProps> = (props) => (
  <ReactFlowProvider>
    <InnerPane {...props} />
  </ReactFlowProvider>
);

export default SystemDesignPane;
